/**
 * Header styles — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/Headers.tsx}.
 *
 * <p>Seven public styles ship here:
 * <ul>
 *   <li>{@code classic}   — safe default</li>
 *   <li>{@code glass}     — sticky, translucent</li>
 *   <li>{@code split}     — two-row utility-then-main (BTK-style)</li>
 *   <li>{@code minimal}   — slim, brand-only, icon search</li>
 *   <li>{@code cinematic} — dark, content-first (Netflix)</li>
 *   <li>{@code cinematic-immersive} — overlays content</li>
 *   <li>{@code sidebar}   — vertical left rail (streaming app)</li>
 * </ul>
 *
 * <p>Two more styles are dispatchable but DELEGATED:
 * <ul>
 *   <li>{@code btk} / {@code sidebar-collapsible} — tenant-private,
 *       live in {@code @akademi/theme-btk}. {@link HeaderRenderer}
 *       calls the optional {@code resolveCustomHeader} prop to let
 *       hosts plug them in. Falls back to {@code ClassicHeader}
 *       when no resolver answers.</li>
 *   <li>{@code none} — returns null (headerless shell).</li>
 * </ul>
 *
 * <p>Cross-cutting concerns:
 * <ul>
 *   <li><b>Locale</b> — every authored string flows through
 *       {@code t(value, locale, fallback)}.</li>
 *   <li><b>Permissions</b> — menu items, footer links, top-bar CTAs
 *       carry {@code requireAuth}/{@code requirePerms}; the renderer
 *       filters before render.</li>
 * </ul>
 */
import type { CSSProperties, ReactNode } from 'react';
import type {
  AuthRequirement,
  CurrentUser,
  HeaderConfig,
  HeaderStyle,
  LocalizedString,
  MegaMenuConfig,
  MenuComponentEntry,
  MenuLink,
  ProfileMenuEntry,
  ProfileMenuLink as ProfileMenuLinkEntry,
  SearchSectionConfig,
  ShellConfig,
} from '../../portal';
import {
  DEFAULT_I18N,
  SEARCH_WIDTH_PRESETS_PX,
  filterVisible,
  flattenMenuWithComponents,
  isVisible,
  mergeMenuComponentProps,
  t,
  useMessages,
} from '../../portal';
import type { ShellMessages } from '../../portal';
import { ThemeToggle } from '../widgets/ThemeToggle';
import { LanguageSwitcher } from '../widgets/LanguageSwitcher';
import { MegaMenuLink } from '../widgets/MegaMenuLink';
import {
  ProfileMenu,
  ProfileMenuEntries,
  type ProfileMenuItem,
  type ProfileMenuLinkItem,
} from '../widgets/ProfileMenu';
import { NotificationsBell } from '../widgets/NotificationsBell';
import { GamificationPointsBadge } from '../widgets/GamificationPointsBadge';
import { LiveSearchBox } from '../widgets/LiveSearchBox';
import { SearchIconTriggerClient } from '../widgets/SearchIconTriggerClient';
import { PillActiveMenuLinks } from '../widgets/PillActiveMenu';
import { filterPillItems } from '../menu/pillMenu';
import { hasDropdownContent, hasFeaturedPromo } from '../menu/menuDropdown';
import { getMenuComponent } from '../menu/menuComponents';
import { BlockIcon, guessSidebarIcon } from '../icons/sidebarIcons';
import { HeaderActions } from './HeaderActions';
import { SidebarCollapsibleHeader } from './SidebarCollapsibleHeader';
import { PortalLink } from '../../portal/PortalLink';

export interface HeaderProps {
  config: ShellConfig;
  tenantHref: (href: string | undefined) => string;
  currentUser: CurrentUser | null;
  locale: string;
  setLocaleAction: (form: FormData) => void | Promise<void>;
  /** Tenant slug — propagated to client-only widgets that can't
   *  receive functions across the RSC boundary. */
  tenantSlug: string;
  /** Optional override for the header's search affordance. When set,
   *  the header renders this node where its default
   *  {@code LiveSearchBox} would appear — replacing the global tenant
   *  search box. */
  searchOverride?: ReactNode;
  /** Per-render override for the shell's {@code categoriesMenu}
   *  component (when the active shell config includes one). */
  categoriesMenuOverride?: { feedSource: 'courses' | 'blog' | 'video' | 'micro' };
  /** Tenant-private header resolver. Hosts that ship tenant-private
   *  chrome (e.g. {@code @akademi/theme-btk}) pass a function here.
   *  When the operator picks a style that's not in the built-in
   *  switch ({@code btk}, {@code sidebar-collapsible}, etc.), the
   *  resolver is called; returning a node renders it, returning
   *  null falls through to {@code ClassicHeader}. */
  resolveCustomHeader?: (style: string, props: HeaderProps) => ReactNode | null;
  /** Numeric tenant ID — forwarded to {@code LiveSearchBox} for the
   *  catalog search API path. Host reads this from {@code client.getTenantId()}. */
  tenantId?: string | number;
  /** Full path to the catalog search proxy route (includes app base path).
   *  E.g. BASEPATH + '/api/catalog/search'. */
  catalogApiPath?: string;
}

// =============================================================================
// HeaderRenderer — top-level dispatcher on shell.header.style
// =============================================================================

/**
 * Whether the whole header passes its {@code requireAuth} /
 * {@code requirePerms} gate (the "Visible to" control in the admin
 * Shell editor's Header → Advanced group).
 *
 * <p>Exported because {@link HeaderRenderer} returning null isn't
 * enough on its own: {@code SiteShellPresenter} also has to know, so a
 * gated-out sidebar header doesn't leave an empty rail column (and its
 * SidebarTopBar / SidebarMobileBar companions) behind. Absent config
 * = visible, so tenants that never touch the field are unaffected.
 */
export function isHeaderVisible(config: ShellConfig, currentUser: CurrentUser | null): boolean {
  const header = config.header;
  if (!header) return true;
  return isVisible(
    {
      requireAuth: header.requireAuth,
      requirePerms: header.requirePerms,
      requireDynamicGroupIds: header.requireDynamicGroupIds,
      requireSecurityGroupIds: header.requireSecurityGroupIds,
    },
    currentUser,
  );
}

export function HeaderRenderer(props: HeaderProps) {
  if (!isHeaderVisible(props.config, props.currentUser)) return null;
  const style: HeaderStyle = props.config.header?.style ?? 'classic';
  switch (style) {
    case 'none':
      return null;
    case 'glass':
      return <GlassHeader {...props} />;
    case 'split':
      return <SplitHeader {...props} />;
    case 'minimal':
      return <MinimalHeader {...props} />;
    case 'cinematic':
      return <CinematicHeader {...props} />;
    case 'cinematic-immersive':
      return <CinematicImmersiveHeader {...props} />;
    case 'sidebar':
      return <SidebarHeader {...props} />;
    case 'sidebar-collapsible':
      // Client component — receives the serializable subset of props.
      // Function props (tenantHref, setLocaleAction) can't cross the
      // server→client boundary in App Router.
      return (
        <SidebarCollapsibleHeader
          config={props.config}
          tenantSlug={props.tenantSlug}
          currentUser={props.currentUser}
          locale={props.locale}
          categoriesMenuOverride={props.categoriesMenuOverride}
        />
      );
    case 'classic':
      return <ClassicHeader {...props} />;
  }
  // Tenant-private or unknown style — delegate to the host resolver
  // (e.g. tenant-btk's `btk`, `sidebar-collapsible`). If the resolver
  // returns null (or no resolver was supplied), fall through to the
  // safe default rather than rendering nothing.
  const custom = props.resolveCustomHeader?.(style, props);
  if (custom !== null && custom !== undefined) return custom;
  return <ClassicHeader {...props} />;
}

// =============================================================================
// Shared atoms
// =============================================================================

export function Brand({
  brand,
  tenantHref,
  big,
  locale,
  fallbackLocale,
  inverse,
  logoHeight,
  className,
}: {
  brand: ShellConfig['brand'];
  tenantHref: HeaderProps['tenantHref'];
  big?: boolean;
  locale: string;
  fallbackLocale: string;
  /** Use light text colours on dark-chrome headers. Also selects the
   *  dark-surface logo variant when {@code brand.logoDark} is set. */
  inverse?: boolean;
  /** Explicit logo height override in px. Wins over {@code big}. */
  logoHeight?: number;
  /** Extra class on the brand link. Top-bar headers pass
   *  {@code lms-header-brand} so the mobile layout can absolute-center
   *  the mark between the hamburger and the profile button (see
   *  "Mobile header layout" in portal.css). Deliberately NOT applied to
   *  the drawer's own brand or the sidebar rail — those stay in flow. */
  className?: string;
}) {
  const b = brand ?? {};
  const name = t(b.name, locale, fallbackLocale).trim();
  const tagline = t(b.tagline, locale, fallbackLocale).trim();
  // logo may be stored as { light, dark } by ThemedImageInput — unwrap if so
  const rawLogo = b.logo as string | { light?: string; dark?: string } | undefined;
  const lightLogo =
    rawLogo && typeof rawLogo === 'object' ? rawLogo.light || rawLogo.dark : rawLogo;
  const rawLogoDark = b.logoDark as string | { light?: string; dark?: string } | undefined;
  const resolvedLogoDark =
    rawLogoDark && typeof rawLogoDark === 'object'
      ? rawLogoDark.dark || rawLogoDark.light
      : rawLogoDark;
  const darkLogo = resolvedLogoDark || lightLogo;
  const hasLogo = !!lightLogo || !!darkLogo;
  const hasName = name.length > 0;

  // Deliberately-blank brand: render nothing rather than a placeholder
  // square + "Brand" text. Tenants who express identity via content
  // instead of a fixed mark get clean chrome.
  if (!hasLogo && !hasName) return <></>;

  const px = logoHeight ?? (big ? 36 : 26);
  // A tenant logo's real aspect ratio is unknown ahead of time, so pinning
  // only `height` (leaving width to resolve from the natural image) still
  // lets the header's width jump once the image loads — layout shift,
  // worse under slow network. Reserving a fixed BOX (both dimensions) with
  // `object-fit: contain` inside gives a zero-shift guarantee regardless of
  // the real ratio, at the cost of letterboxing an unusually wide/narrow
  // logo — an acceptable trade a slug of shift isn't.
  const boxWidth = Math.round(px * 3.5);
  const imgFitStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'left center',
    display: 'block',
  } as const;

  return (
    <PortalLink
      href={tenantHref(b.logoHref ?? '/')}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
        color: 'inherit',
        // Never let the brand mark get squeezed by the flex header row
        // when menu/search/controls run out of room — it's the one
        // element that must always stay fully visible.
        flexShrink: 0,
      }}
    >
      {hasLogo &&
        (inverse ? (
          // Forced-dark chrome (cinematic / BTK): surface is dark
          // regardless of theme. Pick the dark variant directly —
          // no dual-render / CSS swap, dodges the brief layout reflow
          // before CSS resolves on always-dark headers.
          <div style={{ width: boxWidth, height: px }}>
            <img
              src={darkLogo}
              alt={name || 'logo'}
              style={imgFitStyle}
              fetchPriority="high"
              decoding="async"
            />
          </div>
        ) : b.logoDark ? (
          // Theme-driven surface: ship both, CSS picks by data-theme.
          // The .lms-logo--light / --dark classes flip via the host's
          // global CSS; the theme bootstrap stamps data-theme before
          // paint so there's no flicker.
          <>
            {/* No `display` in these styles — the .lms-logo--light /
                --dark classes own visibility via CSS (data-theme
                toggle). An inline `display` would out-specificity
                the class and both logos would show at once. */}
            <div className="lms-logo--light" style={{ width: boxWidth, height: px }}>
              <img
                src={lightLogo}
                alt={name || 'logo'}
                style={imgFitStyle}
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="lms-logo--dark" style={{ width: boxWidth, height: px }}>
              <img
                src={b.logoDark}
                alt={name || 'logo'}
                style={imgFitStyle}
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </>
        ) : (
          <div style={{ width: boxWidth, height: px }}>
            <img
              src={lightLogo}
              alt={name || 'logo'}
              style={imgFitStyle}
              fetchPriority="high"
              decoding="async"
            />
          </div>
        ))}
      {hasName && (
        <span style={{ lineHeight: 1.05 }}>
          <span
            style={{
              display: 'block',
              fontWeight: 700,
              fontSize: big ? 'var(--font-size-lg, 18px)' : 'var(--font-size-md, 16px)',
              color: inverse
                ? 'var(--color-text-on-brand, #ffffff)'
                : 'var(--color-text-primary, #0f1218)',
              fontFamily: 'var(--font-family-display, inherit)',
            }}
          >
            {name}
          </span>
          {tagline && (
            <span
              style={{
                display: 'block',
                marginTop: 2,
                fontSize: 'var(--font-size-xs, 12px)',
                fontFamily: 'var(--font-family-body, inherit)',
                letterSpacing: 0,
                color: inverse ? 'rgba(255,255,255,0.55)' : 'var(--color-text-secondary, #5f6675)',
                textTransform: 'uppercase',
              }}
            >
              {tagline}
            </span>
          )}
        </span>
      )}
    </PortalLink>
  );
}

interface MenuItem {
  label: LocalizedString;
  href: string;
  badge?: LocalizedString;
  megamenu?: MegaMenuConfig;
  dropdownStyle?: 'mega' | 'flat';
  active?: boolean;
  icon?: string;
}

function MenuLinkRow({
  item,
  tenantHref,
  dim,
  locale,
  fallbackLocale,
  currentUser,
  inverse,
  tenantSlug,
  pillStyle,
  showIcon,
}: {
  item: MenuItem;
  tenantHref: HeaderProps['tenantHref'];
  dim?: boolean;
  locale: string;
  fallbackLocale: string;
  currentUser: CurrentUser | null;
  inverse?: boolean;
  tenantSlug: string;
  pillStyle?: boolean;
  /** Renders `item.icon` before the label — on by default, matching
   *  sidebar rows; set `header.showMenuIcons: false` to suppress it in
   *  top-bar styles. See `HeaderConfig.showMenuIcons`. */
  showIcon?: boolean;
}) {
  const label = t(item.label, locale, fallbackLocale);
  const badge = t(item.badge, locale, fallbackLocale);
  // 20px matches the search/language action icons in the header's right
  // cluster (SearchIconTriggerClient/LanguageSwitcher's GlobeIcon), so a
  // collapsed icon-only row reads as one consistent icon row.
  //
  // The icon itself always renders (falling back to a guessed icon when
  // the entry has none) — `showMenuIcons: false` only hides it at desktop
  // width via `lms-menu-icon--desktop-hidden` below. Below the
  // `lms-header-nav-desktop` breakpoint (1023px, see portal.css) the row
  // collapses to icon-only regardless of that flag, so the icon must
  // exist in the DOM to force back on.
  const icon = (
    <span className={showIcon ? 'lms-menu-icon' : 'lms-menu-icon lms-menu-icon--desktop-hidden'}>
      <BlockIcon value={item.icon || guessSidebarIcon(label)} size={20} />
    </span>
  );
  const labelText = <span className="lms-menu-label-text">{label}</span>;

  const hasDropdown = hasDropdownContent(item.megamenu, currentUser, locale, fallbackLocale);

  if (hasDropdown && item.megamenu) {
    const layout: 'mega' | 'flat' = item.dropdownStyle === 'flat' ? 'flat' : 'mega';
    const featuredVisible =
      layout === 'mega' && hasFeaturedPromo(item.megamenu, currentUser, locale, fallbackLocale);
    return (
      <MegaMenuLink
        label={label}
        href={item.href}
        badge={badge}
        dim={dim}
        icon={item.icon}
        megamenu={item.megamenu}
        tenantSlug={tenantSlug}
        currentUser={currentUser}
        locale={locale}
        fallbackLocale={fallbackLocale}
        inverse={inverse}
        panelLayout={layout}
        showFeaturedPromo={featuredVisible}
        showIcon={showIcon}
      />
    );
  }

  if (pillStyle) {
    const isActive = item.active === true;
    return (
      <PortalLink
        href={tenantHref(item.href)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 'var(--font-size-sm, 14px)',
          fontWeight: isActive ? 600 : 500,
          padding: isActive ? '6px 14px' : '6px 8px',
          borderRadius: 999,
          background: isActive ? 'rgba(255,255,255,0.14)' : 'transparent',
          color: isActive ? 'var(--color-text-on-brand, #fff)' : 'rgba(255,255,255,0.78)',
          textDecoration: 'none',
          transition: 'background 150ms ease, color 150ms ease',
          // Never wrap/compress — the nav row scrolls horizontally
          // instead (see the <nav> element's overflowX) once items no
          // longer fit, rather than squeezing text onto 2-3 lines and
          // shrinking the icon next to it.
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {icon}
        {labelText}
        {badge && (
          <span
            style={{
              background: 'var(--color-brand-500, #e50914)',
              color: 'var(--color-text-on-brand, #fff)',
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 700,
              padding: 'var(--space-px, 1px) var(--space-1, 4px)',
              borderRadius: 'var(--radius-sm, 4px)',
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
            }}
          >
            {badge}
          </span>
        )}
      </PortalLink>
    );
  }

  return (
    <PortalLink
      href={tenantHref(item.href)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 'var(--font-size-sm, 14px)',
        fontWeight: 500,
        color: inverse ? 'rgba(255,255,255,0.85)' : 'var(--color-text-primary, #0f1218)',
        textDecoration: 'none',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {labelText}
      {badge && (
        <span
          style={{
            background: 'var(--color-brand-500, #3b5bdb)',
            color: 'var(--color-text-on-brand, #fff)',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 700,
            padding: 'var(--space-px, 1px) var(--space-1, 4px)',
            borderRadius: 'var(--radius-sm, 4px)',
            letterSpacing: 'var(--font-tracking-wider, 0.04em)',
          }}
        >
          {badge}
        </span>
      )}
    </PortalLink>
  );
}

/**
 * Whether the header search runs in catalog mode, and with what configuration.
 *
 * <p>Catalog mode is the default and stays the default. Tenants whose
 * backend-persisted shell config predates the `search` section would otherwise
 * silently fall back to suggest mode, which for a portal has no adapter wired up
 * and so never fires a request — a search box that does nothing.
 *
 * <p>A host whose content is not the learning catalog opts out with
 * `search.mode: 'suggest'`, which is explicit rather than inferred: a commerce
 * storefront's products are not courses, blog posts or employees, so there is
 * nothing for the catalog dropdown's tabs to show and its endpoint is not one the
 * storefront serves. Returning undefined here puts the widget in suggest mode,
 * where it asks the host's own SearchSource instead.
 */
function resolveCatalogSearch(config: ShellConfig): SearchSectionConfig | undefined {
  if (config.search?.mode === 'suggest') return undefined;
  return config.search ?? (config.header?.showSearch === false ? undefined : {});
}

function SearchInput({
  tenantHref,
  variant = 'pill',
  inverse = false,
  tenantSlug,
  fillWidth = false,
  config,
  locale,
  tenantId,
  catalogApiPath,
}: {
  tenantHref: HeaderProps['tenantHref'];
  variant?: 'pill' | 'flush' | 'icon';
  inverse?: boolean;
  tenantSlug: string;
  fillWidth?: boolean;
  config: ShellConfig;
  locale: string;
  tenantId?: string | number;
  catalogApiPath?: string;
}) {
  const catalogSearch = resolveCatalogSearch(config);
  const localeFallback = (config.i18n ?? DEFAULT_I18N).default;
  // Operator-authored placeholder for the default (suggest-mode) search
  // box. Empty when unset so the widget falls back to its built-in
  // localized default; catalog mode overrides it with its own labels.
  const searchPlaceholder =
    t(config.header?.searchPlaceholder, locale, localeFallback) || undefined;

  if (variant === 'icon') {
    return (
      <SearchIconTriggerClient
        tenantSlug={tenantSlug}
        resultsHref={config.search?.resultsHref}
        inverse={inverse}
        catalogSearch={catalogSearch}
        placeholder={searchPlaceholder}
        locale={locale}
        localeFallback={localeFallback}
        tenantId={tenantId}
        catalogApiPath={catalogApiPath}
      />
    );
  }
  // tenantHref is accepted in props for caller ergonomics but isn't
  // forwarded — the client widget navigates via useRouter. Functions
  // can't cross the server→client boundary in App Router.
  void tenantHref;
  return (
    <LiveSearchBox
      tenantSlug={tenantSlug}
      resultsHref={config.search?.resultsHref}
      variant="inline"
      expandToDialog={!catalogSearch?.searchInline}
      inverse={inverse}
      flush={variant === 'flush'}
      fillWidth={fillWidth}
      width={SEARCH_WIDTH_PRESETS_PX[catalogSearch?.width ?? 'default']}
      catalogSearch={catalogSearch}
      placeholder={searchPlaceholder}
      locale={locale}
      localeFallback={localeFallback}
      tenantId={tenantId}
      catalogApiPath={catalogApiPath}
    />
  );
}

/**
 * Renders both the pill and icon-trigger search variants, letting CSS
 * (container query, see `.lms-header-search-pill`/`.lms-header-search-icon`
 * in portal.css) pick whichever one currently fits the header row —
 * regardless of viewport size, since the row can run out of room at
 * any width depending on how many menu items / actions a tenant has
 * configured. Only used for the 'pill'/'flush' placements; an operator
 * who explicitly picked "Show as icon" gets {@link SearchInput} directly.
 */
function ResponsiveSearchInput(
  props: Omit<Parameters<typeof SearchInput>[0], 'variant'> & { variant?: 'pill' | 'flush' },
) {
  const { fillWidth, ...rest } = props;
  return (
    <>
      <span
        className="lms-header-search-pill"
        style={fillWidth ? { flex: 1, minWidth: 0 } : undefined}
      >
        <SearchInput {...rest} fillWidth={fillWidth} />
      </span>
      <span className="lms-header-search-icon">
        <SearchInput {...rest} variant="icon" fillWidth={false} />
      </span>
    </>
  );
}

/** Whether the header search should render centered in the row's empty
 *  space rather than the flex spacer. Only meaningful for the inline
 *  pill placement — icon-trigger and hero-only search never move. */
function isSearchCenterActive(config: ShellConfig): boolean {
  const header = config.header ?? {};
  if (header.searchCenter !== true) return false;
  const placement = header.searchPlacement as
    | 'header'
    | 'topbar'
    | 'icon'
    | 'hero-only'
    | undefined;
  if (placement === 'icon' || placement === 'hero-only') return false;
  return !!(config.search?.showSearch ?? header.showSearch);
}

/**
 * Centers the search input in the header's flex spacer when
 * {@code header.searchCenter} is active; otherwise renders the plain
 * spacer it replaces. Used by the top-bar header styles in place of a
 * bare {@code <div style={{ flex: 1 }} />}.
 */
function HeaderSearchCenter({
  config,
  tenantHref,
  tenantSlug,
  locale,
  tenantId,
  catalogApiPath,
  inverse = false,
}: {
  config: ShellConfig;
  tenantHref: HeaderProps['tenantHref'];
  tenantSlug: string;
  locale: string;
  tenantId?: string | number;
  catalogApiPath?: string;
  inverse?: boolean;
}) {
  if (!isSearchCenterActive(config)) return <div style={{ flex: 1 }} />;
  return (
    // Outer div is the unconditional flex:1 spacer — it must NEVER be
    // hidden, mobile included, or MobileMenu loses the space that
    // pins it to the row's right edge. Only the search content inside
    // (a menu/controls "desktop chrome" concern, already replaced by
    // MobileMenu's own drawer search below 768px) gets hidden.
    <div
      className="lms-header-search-center"
      style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}
    >
      <div
        className="lms-header-desktop-only lms-header-search-center-content"
        style={{
          width: '100%',
          maxWidth: 480,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <ResponsiveSearchInput
          tenantHref={tenantHref}
          tenantSlug={tenantSlug}
          inverse={inverse}
          config={config}
          locale={locale}
          tenantId={tenantId}
          catalogApiPath={catalogApiPath}
        />
      </div>
    </div>
  );
}

function SignInLink({
  tenantHref,
  currentUser,
  inverse,
  locale,
}: {
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  inverse?: boolean;
  locale: string;
}) {
  const messages = useMessages();
  const href = currentUser ? '/sign-out' : '/sign-in';
  const label = currentUser
    ? chromeText(messages, 'signOut', locale)
    : chromeText(messages, 'signIn', locale);
  return (
    <PortalLink
      href={tenantHref(href)}
      style={{
        fontSize: 'var(--font-size-sm, 14px)',
        color: inverse ? 'rgba(255,255,255,0.75)' : 'var(--color-text-secondary, #5f6675)',
        textDecoration: 'none',
        fontWeight: 500,
      }}
    >
      {label}
    </PortalLink>
  );
}

/** Resolve one config-authored profile-menu link (localized label,
 *  bare href, icon key) into the plain-value shape {@code ProfileMenu}
 *  renders. Shared by top-level links and a group's nested links. */
function resolveProfileMenuLink(
  item: ProfileMenuLinkEntry,
  locale: string,
  tenantHref: HeaderProps['tenantHref'],
): ProfileMenuLinkItem {
  return {
    label: t(item.label, locale, DEFAULT_I18N.default ?? 'en'),
    href: tenantHref(item.href),
    icon: item.icon ? <BlockIcon value={item.icon} size={18} /> : undefined,
  };
}

function resolveProfileMenuEntry(
  item: ProfileMenuEntry,
  locale: string,
  tenantHref: HeaderProps['tenantHref'],
  currentUser: CurrentUser | null,
): ProfileMenuItem {
  if (item.kind === 'group') {
    return {
      kind: 'group',
      label: t(item.label, locale, DEFAULT_I18N.default ?? 'en'),
      icon: item.icon ? <BlockIcon value={item.icon} size={18} /> : undefined,
      color: item.color,
      links: (item.links ?? [])
        .filter((link) => isVisible(link, currentUser))
        .map((link) => resolveProfileMenuLink(link, locale, tenantHref)),
    };
  }
  return resolveProfileMenuLink(item, locale, tenantHref);
}

/** Top-level {@code header.profileMenu} rows visible to {@code currentUser} —
 *  the "Visible to" gate (requireAuth / requirePerms / requireDynamicGroupIds /
 *  requireSecurityGroupIds) every other menu shape in this file honours. */
function visibleProfileMenu(
  entries: ProfileMenuEntry[] | undefined,
  currentUser: CurrentUser | null,
): ProfileMenuEntry[] {
  return (entries ?? []).filter((item) => isVisible(item, currentUser));
}

function AuthAffordance({
  header,
  tenantHref,
  currentUser,
  locale,
  inverse,
}: {
  header: HeaderConfig;
  tenantHref: HeaderProps['tenantHref'];
  tenantSlug: string;
  currentUser: CurrentUser | null;
  locale: string;
  inverse?: boolean;
}) {
  const showSignIn = header.showSignIn ?? true;
  const showProfile = header.showProfile ?? true;
  const showNotifications = (header['showNotifications'] as boolean | undefined) ?? true;
  const showGamificationPoints = (header['showGamificationPoints'] as boolean | undefined) ?? false;

  if (currentUser) {
    const profileMenuExtraItems = visibleProfileMenu(header.profileMenu, currentUser).map((item) =>
      resolveProfileMenuEntry(item, locale, tenantHref, currentUser),
    );
    const profileMenuStyle = header.profileMenuStyle ?? 'list';

    return (
      <>
        {showGamificationPoints && <GamificationPointsBadge inverse={inverse} />}
        {showNotifications && <NotificationsBell locale={locale} inverse={inverse} />}
        {showProfile && (
          <ProfileMenu
            currentUser={currentUser}
            profileHref={tenantHref('/profile')}
            signOutHref={tenantHref('/sign-out')}
            extraItems={profileMenuExtraItems}
            locale={locale}
            inverse={inverse}
            variant={profileMenuStyle}
          />
        )}
      </>
    );
  }

  return showSignIn ? (
    <SignInLink
      tenantHref={tenantHref}
      currentUser={currentUser}
      inverse={inverse}
      locale={locale}
    />
  ) : (
    <></>
  );
}

/**
 * Right-side cluster shared across every horizontal header style:
 * search + CTA + language switcher + theme toggle + auth.
 *
 * <p>{@code omitSearch} is set by {@link SplitHeader} which owns its
 * own centre-column search; {@code inverseChrome} is set by cinematic
 * headers so child controls use light text.
 */
function HeaderControls({
  config,
  tenantHref,
  tenantSlug,
  currentUser,
  locale,
  setLocaleAction,
  tenantId,
  catalogApiPath,
  searchVariant = 'pill',
  omitSearch = false,
  inverseChrome = false,
  searchOverride,
}: HeaderProps & {
  searchVariant?: 'pill' | 'icon';
  omitSearch?: boolean;
  inverseChrome?: boolean;
}) {
  const header = config.header ?? {};
  const i18n = config.i18n ?? DEFAULT_I18N;
  const showSwitcher = (i18n.showSwitcher ?? false) && (i18n.available?.length ?? 1) > 1;

  const showOverride = !omitSearch && !!searchOverride;
  const showDefault =
    !omitSearch &&
    !searchOverride &&
    !!(config.search?.showSearch ?? header.showSearch) &&
    header.searchPlacement !== ('hero-only' as HeaderConfig['searchPlacement']) &&
    !isSearchCenterActive(config);

  const explicitIcon =
    (header.searchPlacement as 'header' | 'topbar' | 'icon' | undefined) === 'icon';

  // Tablet-only compact search icon. The centered pill search
  // (HeaderSearchCenter) owns desktop widths; between 768-1023px it
  // hides (see `.lms-header-search-center-content` in portal.css) and
  // this icon — positioned right next to the language switcher — takes
  // over instead, so the icon-only nav row (also <1024px) isn't left
  // with a floating search column of its own between it and the
  // language/theme/auth cluster.
  const centerActive = !omitSearch && !searchOverride && isSearchCenterActive(config);

  return (
    <span className="lms-header-controls-desktop">
      {showOverride && searchOverride}
      {showDefault &&
        (explicitIcon || searchVariant === 'icon' ? (
          <SearchInput
            tenantHref={tenantHref}
            tenantSlug={tenantSlug}
            variant="icon"
            inverse={inverseChrome}
            config={config}
            locale={locale}
            tenantId={tenantId}
            catalogApiPath={catalogApiPath}
          />
        ) : (
          <ResponsiveSearchInput
            tenantHref={tenantHref}
            tenantSlug={tenantSlug}
            inverse={inverseChrome}
            config={config}
            locale={locale}
            tenantId={tenantId}
            catalogApiPath={catalogApiPath}
          />
        ))}
      <HeaderActions
        header={header}
        locale={locale}
        i18nDefault={i18n.default}
        currentUser={currentUser}
        tenantHref={tenantHref}
      />
      {centerActive && (
        <span className="lms-header-search-tablet">
          <SearchInput
            tenantHref={tenantHref}
            tenantSlug={tenantSlug}
            variant="icon"
            inverse={inverseChrome}
            config={config}
            locale={locale}
            tenantId={tenantId}
            catalogApiPath={catalogApiPath}
          />
        </span>
      )}
      {showSwitcher && (
        <LanguageSwitcher
          i18n={i18n}
          activeLocale={locale}
          setLocaleAction={setLocaleAction}
          inverse={inverseChrome}
        />
      )}
      {header.showThemeToggle && <ThemeToggle variant={header.themeToggleStyle} />}
      <AuthAffordance
        header={header}
        tenantHref={tenantHref}
        tenantSlug={tenantSlug}
        currentUser={currentUser}
        locale={locale}
        inverse={inverseChrome}
      />
    </span>
  );
}

/**
 * Built-in FALLBACK dictionary for the header chrome strings the
 * renderer hard-codes (mobile-drawer section headings, auth links, and
 * the nav landmark a11y label). The real, all-locale copy comes from
 * the host's {@link ShellMessages} (keyed {@code header.<key>}, sourced
 * from the app's i18n catalog) via {@link chromeText}; this dict is
 * only the safety net when no host catalog is mounted. Same
 * tr/en-with-en-fallback shape as {@code ProfileMenu}'s own
 * {@code tr()}. Named {@code chromeTr} to avoid colliding with the
 * platform {@code t()} import used for tenant-authored
 * {@link LocalizedString}s.
 */
function chromeTr(key: string, locale: string): string {
  const dict: Record<string, Record<string, string>> = {
    menu: { tr: 'Menü', en: 'Menu' },
    preferences: { tr: 'Tercihler', en: 'Preferences' },
    language: { tr: 'Dil', en: 'Language' },
    account: { tr: 'Hesap', en: 'Account' },
    viewProfile: { tr: 'Profili görüntüle', en: 'View profile' },
    signIn: { tr: 'Giriş yap', en: 'Sign in' },
    signOut: { tr: 'Çıkış yap', en: 'Sign out' },
    toggleNav: { tr: 'Menüyü aç/kapat', en: 'Toggle navigation menu' },
    navigation: { tr: 'Gezinme', en: 'Navigation' },
    primaryNav: { tr: 'Ana menü', en: 'Primary' },
  };
  return dict[key]?.[locale] ?? dict[key]?.['en'] ?? key;
}

/**
 * Resolve a header chrome string: host-injected {@link ShellMessages}
 * (keyed {@code header.<key>}, localized for every app locale) when
 * present, else the built-in {@link chromeTr} fallback for the active
 * locale. {@code messages} comes from {@code useMessages()} at the
 * calling component.
 */
function chromeText(messages: ShellMessages | null, key: string, locale: string): string {
  return messages?.[`header.${key}`] ?? chromeTr(key, locale);
}

/**
 * Mobile-only account affordance — the right-hand slot of the
 * {@code [hamburger | logo | profile]} mobile header row.
 *
 * <p>Signed in: the SAME {@link ProfileMenu} dropdown desktop renders
 * (portaled, fixed-positioned — it can't be clipped by the header), so
 * the tenant's authored {@code header.profileMenu} rows and sign-out
 * are one tap away instead of buried in the nav drawer. Signed out: a
 * 40px round sign-in chip matching the other header action chips.
 *
 * <p>Rendered from {@link MobileMenu} rather than at every header call
 * site so all seven top-bar styles (plus {@link SidebarMobileBar}) pick
 * the layout up from one place.
 */
function MobileProfileButton({
  config,
  tenantHref,
  currentUser,
  locale,
  inverse,
}: {
  config: ShellConfig;
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  locale: string;
  inverse?: boolean;
}) {
  const header = config.header ?? {};
  const messages = useMessages();
  const showProfile = header.showProfile ?? true;
  const showSignIn = header.showSignIn ?? true;

  if (currentUser) {
    if (!showProfile) return null;
    return (
      <span className="lms-header-mobile-only lms-header-mobile-profile">
        <ProfileMenu
          currentUser={currentUser}
          profileHref={tenantHref('/profile')}
          signOutHref={tenantHref('/sign-out')}
          extraItems={visibleProfileMenu(header.profileMenu, currentUser).map((item) =>
            resolveProfileMenuEntry(item, locale, tenantHref, currentUser),
          )}
          locale={locale}
          inverse={inverse}
          variant={header.profileMenuStyle ?? 'list'}
        />
      </span>
    );
  }

  if (!showSignIn) return null;
  return (
    <span className="lms-header-mobile-only lms-header-mobile-profile">
      <PortalLink
        href={tenantHref('/sign-in')}
        aria-label={chromeText(messages, 'signIn', locale)}
        className="lms-header-mobile-profile-btn"
        style={{
          color: inverse
            ? 'var(--color-text-on-brand, #fff)'
            : 'var(--color-text-primary, #0f1218)',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </PortalLink>
    </span>
  );
}

/**
 * Mobile menu — native {@code <details>}/{@code <summary>} drawer that
 * opens to a full-screen overlay. Hidden above 768px (CSS class
 * {@code lms-header-mobile-only}); below it replaces ALL of the
 * header's affordances at once: nav, search, switcher, toggle,
 * sign-in, profile.
 *
 * <p>Why {@code <details>} and not a JS drawer: headers are server
 * components — making them client would pull MegaMenuLink, ThemeToggle,
 * LanguageSwitcher across the boundary. {@code <details>} is the only
 * zero-JS toggleable disclosure in HTML; keyboard-accessible by
 * default; exposes {@code open} as a styleable attribute selector;
 * works without hydration. Trade-off (no slide-from-side animation)
 * is acceptable vs the alternative of a full client-component refactor.
 */
function MobileMenu({
  config,
  tenantHref,
  currentUser,
  locale,
  fallbackLocale: _fallbackLocale,
  tenantSlug,
  inverse,
  setLocaleAction,
  tenantId,
  catalogApiPath,
}: {
  config: ShellConfig;
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  locale: string;
  fallbackLocale: string;
  tenantSlug: string;
  inverse?: boolean;
  setLocaleAction?: HeaderProps['setLocaleAction'];
  tenantId?: string | number;
  catalogApiPath?: string;
}) {
  const header = config.header ?? {};
  const i18n = config.i18n ?? DEFAULT_I18N;
  const messages = useMessages();
  const showSwitcher = (i18n.showSwitcher ?? false) && (i18n.available?.length ?? 1) > 1;
  const showSearch = !!(
    (config.search?.showSearch ?? header.showSearch) &&
    header.searchPlacement !== ('hero-only' as HeaderConfig['searchPlacement'])
  );
  const visibleMenu = filterVisible(flattenMenuWithComponents(header.menu), currentUser);
  const showMenuIcons = header.showMenuIcons !== false;

  const showSignIn = header.showSignIn ?? true;
  const showProfile = header.showProfile ?? true;
  const fallback = i18n.default;
  const mobileCartAction = (header.actions ?? []).find((action) => action.href === '/cart');

  return (
    <>
      <details
        className="lms-header-mobile-only lms-mobile-drawer"
        // Close the drawer when any link inside it is clicked. <details>
        // only closes natively on <summary> clicks, so navigation links
        // leave it open. The shell renders inside a client subtree
        // (SiteShellBlock is `'use client'`), so a delegated onClick runs
        // after hydration — a raw <script> here would never execute on the
        // client and React warns about it ("Encountered a script tag …").
        onClick={(e) => {
          const link = (e.target as HTMLElement | null)?.closest?.('a');
          if (link && e.currentTarget.contains(link)) {
            e.currentTarget.removeAttribute('open');
          }
        }}
      >
        <summary
          aria-label={chromeText(messages, 'toggleNav', locale)}
          className="lms-mobile-drawer-toggle"
          style={{
            color: inverse
              ? 'var(--color-text-on-brand, #fff)'
              : 'var(--color-text-primary, #0f1218)',
          }}
        >
          <svg
            className="lms-mobile-drawer-icon-open"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          <svg
            className="lms-mobile-drawer-icon-close"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </summary>

        <div
          className="lms-mobile-drawer-panel"
          role="dialog"
          aria-modal="true"
          aria-label={chromeText(messages, 'navigation', locale)}
        >
          <div className="lms-mobile-drawer-brand">
            <Brand
              brand={config.brand}
              tenantHref={tenantHref}
              locale={locale}
              fallbackLocale={fallback}
              logoHeight={config.brand?.logoHeight}
            />
          </div>
          <div className="lms-mobile-drawer-scroll">
            {showSearch && (
              <section className="lms-mobile-drawer-section">
                <LiveSearchBox
                  tenantSlug={tenantSlug}
                  variant="inline"
                  expandToDialog
                  fillWidth
                  resultsHref={config.search?.resultsHref}
                  catalogSearch={resolveCatalogSearch(config)}
                  placeholder={t(header.searchPlaceholder, locale, fallback) || undefined}
                  locale={locale}
                  localeFallback={fallback}
                  tenantId={tenantId}
                  catalogApiPath={catalogApiPath}
                />
              </section>
            )}

            {(showSwitcher || header.showThemeToggle) && (
              <section className="lms-mobile-drawer-section">
                <div className="lms-mobile-drawer-section-label">
                  {chromeText(messages, 'preferences', locale)}
                </div>
                <div className="lms-mobile-drawer-controls">
                  {showSwitcher && setLocaleAction && (
                    <div
                      className="lms-mobile-drawer-language"
                      // LanguageSwitcher owns its own trigger <button> (icon
                      // only, no visible text) — forward clicks on the label
                      // to that button instead of duplicating its open/close
                      // state here. Guard on `closest('button')` so clicks
                      // that already landed on the button don't re-fire it.
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('button')) return;
                        e.currentTarget.querySelector('button')?.click();
                      }}
                    >
                      <LanguageSwitcher
                        i18n={i18n}
                        activeLocale={locale}
                        setLocaleAction={setLocaleAction}
                      />
                      <span className="lms-mobile-drawer-language-label">
                        {chromeText(messages, 'language', locale)}
                      </span>
                    </div>
                  )}
                  {header.showThemeToggle && <ThemeToggle variant={header.themeToggleStyle} />}
                </div>
              </section>
            )}

            {visibleMenu.length > 0 && (
              <section className="lms-mobile-drawer-section">
                <div className="lms-mobile-drawer-section-label">
                  {chromeText(messages, 'menu', locale)}
                </div>
                <nav
                  aria-label={chromeText(messages, 'primaryNav', locale)}
                  className="lms-mobile-drawer-links"
                >
                  {visibleMenu.map((entry, i) => {
                    // Component-kind entries (categoriesMenu,
                    // continueWatching, myTeam, …) render a rich
                    // hover-dropdown on desktop. Each component also
                    // supports a `mobile-drawer` placement that renders
                    // its sub-items as a plain list (no fixed-position
                    // overlay) — mount it inside a native <details>
                    // accordion so the drawer stays scrollable/collapsed
                    // instead of dumping every list open at once.
                    if (entry.kind === 'component') {
                      const Comp = getMenuComponent(entry.componentId);
                      if (!Comp) return null;
                      const mergedProps = mergeMenuComponentProps(entry);
                      mergedProps['showMenuIcons'] = showMenuIcons;
                      const cLabel = t(entry.label, locale, fallback) || entry.componentId;
                      // Components carry their trigger icon under different
                      // keys (`icon` for categoriesMenu, `defaultIcon` for
                      // continueWatching / myTeam); fall back to a guessed
                      // icon so every accordion row is visually anchored.
                      const cIcon = entry.icon || entry.defaultIcon || guessSidebarIcon(cLabel);
                      return (
                        <details key={i} className="lms-mobile-drawer-accordion">
                          <summary className="lms-mobile-drawer-accordion-trigger">
                            {showMenuIcons && <BlockIcon value={cIcon} size={16} />}
                            <span>{cLabel}</span>
                          </summary>
                          <div className="lms-mobile-drawer-links lms-mobile-drawer-accordion-panel">
                            <Comp
                              placement="mobile-drawer"
                              collapsed={false}
                              tenantSlug={tenantSlug}
                              locale={locale}
                              fallbackLocale={fallback}
                              currentUser={currentUser}
                              props={mergedProps}
                            />
                          </div>
                        </details>
                      );
                    }
                    // flattenMenuWithComponents already expands `kind: 'group'`
                    // entries into their individual links, so nothing but
                    // MenuLink survives to this point at runtime.
                    const m = entry as MenuLink;
                    const label = t(m.label, locale, fallback);
                    const badge = t(m.badge, locale, fallback);

                    if (
                      hasDropdownContent(m.megamenu, currentUser, locale, fallback) &&
                      m.megamenu
                    ) {
                      const cols = filterVisible(m.megamenu.columns ?? [], currentUser)
                        .map((c) => ({
                          ...c,
                          links: filterVisible(c.links ?? [], currentUser),
                        }))
                        .filter((c) => c.links.length > 0);
                      const singleCol = cols.length === 1;
                      // Same native <details>/<summary> accordion as the
                      // `kind: 'component'` branch above (arrow indicator,
                      // zero-JS collapse). A PortalLink trigger here would
                      // either eat the tap silently (parent items only meant
                      // to reveal children, no real href) or fight the
                      // <summary> for the click — <summary> always toggles
                      // regardless, so the label is plain text and only the
                      // panel's links navigate.
                      return (
                        <details key={i} className="lms-mobile-drawer-accordion">
                          <summary className="lms-mobile-drawer-accordion-trigger">
                            {showMenuIcons && (
                              <BlockIcon value={m.icon || guessSidebarIcon(label)} size={16} />
                            )}
                            <span>{label}</span>
                            {badge && <span className="lms-mobile-drawer-badge">{badge}</span>}
                          </summary>
                          <div className="lms-mobile-drawer-megamenu">
                            {cols.map((col, ci) => {
                              const colLabel =
                                !singleCol && col.label ? t(col.label, locale, fallback) : '';
                              return (
                                <div key={ci} className="lms-mobile-drawer-megamenu-col">
                                  {colLabel && (
                                    <div className="lms-mobile-drawer-megamenu-collabel">
                                      {colLabel}
                                    </div>
                                  )}
                                  {col.links.map((l, li) => {
                                    const linkLabel = t(l.label, locale, fallback);
                                    return (
                                      <PortalLink
                                        key={li}
                                        href={tenantHref(l.href)}
                                        className="lms-mobile-drawer-megamenu-link"
                                      >
                                        {showMenuIcons && (
                                          <BlockIcon
                                            value={l.icon || guessSidebarIcon(linkLabel)}
                                            size={16}
                                          />
                                        )}
                                        <span>{linkLabel}</span>
                                      </PortalLink>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      );
                    }

                    return (
                      <PortalLink key={i} href={tenantHref(m.href)}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            minWidth: 0,
                          }}
                        >
                          {showMenuIcons && (
                            <BlockIcon value={m.icon || guessSidebarIcon(label)} size={16} />
                          )}
                          <span>{label}</span>
                        </span>
                        {badge && <span className="lms-mobile-drawer-badge">{badge}</span>}
                      </PortalLink>
                    );
                  })}
                </nav>
              </section>
            )}

            {(header.actions ?? []).length > 0 && (
              <section
                className="lms-mobile-drawer-section"
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <HeaderActions
                  header={header}
                  locale={locale}
                  i18nDefault={i18n.default}
                  currentUser={currentUser}
                  tenantHref={tenantHref}
                />
              </section>
            )}

            {(showSignIn || (showProfile && currentUser)) && (
              <section className="lms-mobile-drawer-section">
                <div className="lms-mobile-drawer-section-label">
                  {chromeText(messages, 'account', locale)}
                </div>
                {showProfile && currentUser && (
                  <PortalLink href={tenantHref('/profile')} className="lms-mobile-drawer-account">
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'var(--color-brand-500, #2563eb)',
                        color: 'var(--color-text-on-brand, #fff)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {currentUser.initials ?? 'U'}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{currentUser.name}</span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--font-size-xs, 12px)',
                          opacity: 0.7,
                        }}
                      >
                        {chromeText(messages, 'viewProfile', locale)}
                      </span>
                    </span>
                  </PortalLink>
                )}
                {/* Extra profile-dropdown rows (Settings, Billing, Help, …) —
                 *  the exact same <ProfileMenuEntries> desktop's <ProfileMenu>
                 *  renders, so 'grid' tenants get the same headed tile
                 *  groups here instead of a plain flattened list. */}
                {showProfile && currentUser && (header.profileMenu ?? []).length > 0 && (
                  <nav
                    aria-label={chromeText(messages, 'account', locale)}
                    className="lms-mobile-drawer-links"
                  >
                    <ProfileMenuEntries
                      items={visibleProfileMenu(header.profileMenu, currentUser).map((item) =>
                        resolveProfileMenuEntry(item, locale, tenantHref, currentUser),
                      )}
                      variant={header.profileMenuStyle ?? 'list'}
                    />
                  </nav>
                )}
                {showSignIn && (
                  <PortalLink
                    href={tenantHref(currentUser ? '/sign-out' : '/sign-in')}
                    className="lms-mobile-drawer-account-action"
                  >
                    {currentUser
                      ? chromeText(messages, 'signOut', locale)
                      : chromeText(messages, 'signIn', locale)}
                  </PortalLink>
                )}
              </section>
            )}
          </div>
        </div>
      </details>
      {/* Keep the highest-priority commerce action in the compact header.
       * Account access remains in the drawer; non-commerce tenants retain
       * the existing account shortcut. */}
      {mobileCartAction ? (
        <span className="lms-header-mobile-only lms-header-mobile-profile">
          <HeaderActions
            header={{ ...header, actions: [mobileCartAction] }}
            locale={locale}
            i18nDefault={i18n.default}
            currentUser={currentUser}
            tenantHref={tenantHref}
          />
        </span>
      ) : (
        <MobileProfileButton
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          inverse={inverse}
        />
      )}
    </>
  );
}

function MenuLinks({
  header,
  tenantHref,
  currentUser,
  locale,
  fallbackLocale,
  dim = false,
  take,
  inverse,
  tenantSlug,
  pillStyle,
  categoriesMenuOverride,
}: {
  header: HeaderConfig;
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  locale: string;
  fallbackLocale: string;
  dim?: boolean;
  take?: number;
  inverse?: boolean;
  tenantSlug: string;
  pillStyle?: boolean;
  categoriesMenuOverride?: HeaderProps['categoriesMenuOverride'];
}) {
  let visible = filterVisible(flattenMenuWithComponents(header.menu), currentUser);
  if (typeof take === 'number') visible = visible.slice(0, take);
  const showMenuIcons = header.showMenuIcons !== false;
  return (
    <>
      {visible.map((m, i) => {
        if (m.kind === 'component') {
          const Comp = getMenuComponent(m.componentId);
          if (!Comp) return null;
          const mergedProps = mergeMenuComponentProps(m);
          // `header.showMenuIcons` is a header-wide toggle covering every
          // entry's icon, not just plain `kind: 'link'` rows — component
          // entries (categoriesMenu's trigger icon, continueWatching /
          // myTeam's `defaultIcon`) need it threaded in too, since they
          // resolve their own icon independently of `MenuLinkRow`.
          mergedProps['showMenuIcons'] = showMenuIcons;
          if (m.componentId === 'categoriesMenu' && categoriesMenuOverride) {
            mergedProps['feedSource'] = categoriesMenuOverride.feedSource;
          }
          return (
            <Comp
              key={i}
              placement="header-horizontal"
              collapsed={false}
              tenantSlug={tenantSlug}
              locale={locale}
              fallbackLocale={fallbackLocale}
              currentUser={currentUser}
              props={
                mergedProps as MenuItem['label'] extends infer _ ? Record<string, unknown> : never
              }
            />
          );
        }
        const link = m as MenuItem;
        return (
          <MenuLinkRow
            key={i}
            item={link}
            tenantHref={tenantHref}
            dim={dim}
            locale={locale}
            fallbackLocale={fallbackLocale}
            currentUser={currentUser}
            inverse={inverse}
            tenantSlug={tenantSlug}
            pillStyle={pillStyle}
            showIcon={showMenuIcons}
          />
        );
      })}
    </>
  );
}

// =============================================================================
// Shared row container
// =============================================================================

const menuGapMap: Record<NonNullable<HeaderConfig['menuGap']>, string> = {
  sm: 'var(--space-2, 8px)',
  md: 'var(--space-4, 16px)',
  lg: 'var(--space-6, 24px)',
  xl: 'var(--space-8, 32px)',
};

/**
 * Applied to each top-bar header style's `<nav>` (menu links) alongside
 * `.lms-header-nav-desktop`/`.lms-header-menu-scroll`. `minWidth: 0`
 * lets the nav shrink below its content's width inside the header's
 * flex row (Brand and the auth/actions cluster are pinned via
 * `flexShrink: 0` and never squeeze); once the nav itself can't shrink
 * any further, it scrolls horizontally (`overflowX: auto`) instead of
 * text-wrapping menu labels or clipping their icons.
 */
const navScrollStyle: CSSProperties = {
  overflowX: 'auto',
  minWidth: 0,
};

/** Style objects that set the {@code --lms-menu-gap} custom property read by
 *  {@link navScrollStyle}'s consumers; {@code CSSProperties} has no index
 *  signature for CSS variables, so this narrow alias replaces `as any`. */
type CSSVarStyle = CSSProperties & { '--lms-menu-gap'?: string };

function containerRowStyle(
  maxWidth?: HeaderConfig['maxWidth'],
): CSSProperties & { containerType?: string; containerName?: string } {
  return {
    // 'theme' reads the same container token page content uses
    // (--app-max-width) so the header row lines up with the page;
    // 'full' stretches the row edge-to-edge.
    maxWidth: maxWidth === 'full' ? 'none' : 'var(--app-max-width, 1200px)',
    margin: '0 auto',
    padding: 'var(--space-3, 12px) clamp(var(--space-3-5, 14px), 2.5vw, var(--space-6, 24px))',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-4, 16px)',
    // Containing block for the mobile row's absolutely-centered brand
    // (see "Mobile header layout" in portal.css). `containerType`
    // below does NOT establish one — container-type: inline-size only
    // contains the inline axis, so an abspos child would resolve
    // against the viewport and land off-header.
    position: 'relative',
    // Lets the header search swap pill → icon based on the ROW's own
    // available width (see .lms-header-search-pill/icon in portal.css)
    // rather than the viewport's — the row can run out of room at any
    // viewport size depending on how many menu items / controls a
    // tenant has configured.
    containerType: 'inline-size',
    containerName: 'lms-header-row',
  };
}

// =============================================================================
// 1. ClassicHeader
// =============================================================================

function ClassicHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <header
      style={{
        ...(header.sticky ? { position: 'sticky', top: 0, zIndex: 30 } : null),
        background: 'var(--color-surface-card, #fff)',
        borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)',
      }}
    >
      <div style={containerRowStyle(header.maxWidth)}>
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          className="lms-header-brand"
          locale={locale}
          fallbackLocale={fallback}
          logoHeight={config.brand?.logoHeight}
        />
        <nav
          aria-label={chromeText(messages, 'primaryNav', locale)}
          className="lms-header-nav-desktop lms-header-menu-scroll"
          style={
            {
              ...navScrollStyle,
              display: 'flex',
              gap: 'var(--lms-menu-gap)',
              '--lms-menu-gap': header.menuGap
                ? menuGapMap[header.menuGap]
                : 'var(--space-4, 16px)',
              marginLeft: 'var(--space-6, 24px)',
            } as CSSVarStyle
          }
        >
          <MenuLinks
            header={header}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            dim
            tenantSlug={props.tenantSlug}
            categoriesMenuOverride={props.categoriesMenuOverride}
          />
        </nav>
        <HeaderSearchCenter
          config={config}
          tenantHref={tenantHref}
          tenantSlug={props.tenantSlug}
          locale={locale}
          tenantId={props.tenantId}
          catalogApiPath={props.catalogApiPath}
        />
        <HeaderControls {...props} />
        <MobileMenu
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          fallbackLocale={fallback}
          setLocaleAction={props.setLocaleAction}
          tenantSlug={props.tenantSlug}
          tenantId={props.tenantId}
          catalogApiPath={props.catalogApiPath}
        />
      </div>
    </header>
  );
}

// =============================================================================
// 2. GlassHeader — sticky translucent
// =============================================================================

function GlassHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)',
      }}
    >
      <div style={{ ...containerRowStyle(header.maxWidth), height: 64 }}>
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          className="lms-header-brand"
          big
          locale={locale}
          fallbackLocale={fallback}
          logoHeight={config.brand?.logoHeight}
        />
        <nav
          aria-label={chromeText(messages, 'primaryNav', locale)}
          className="lms-header-nav-desktop lms-header-menu-scroll"
          style={
            {
              ...navScrollStyle,
              display: 'flex',
              gap: 'var(--lms-menu-gap)',
              '--lms-menu-gap': header.menuGap
                ? menuGapMap[header.menuGap]
                : 'var(--space-6, 24px)',
              marginLeft: 'var(--space-8, 32px)',
            } as CSSVarStyle
          }
        >
          <MenuLinks
            header={header}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            dim
            tenantSlug={props.tenantSlug}
            categoriesMenuOverride={props.categoriesMenuOverride}
          />
        </nav>
        <HeaderSearchCenter
          config={config}
          tenantHref={tenantHref}
          tenantSlug={props.tenantSlug}
          locale={locale}
          tenantId={props.tenantId}
          catalogApiPath={props.catalogApiPath}
        />
        <HeaderControls {...props} />
        <MobileMenu
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          fallbackLocale={fallback}
          setLocaleAction={props.setLocaleAction}
          tenantSlug={props.tenantSlug}
          tenantId={props.tenantId}
          catalogApiPath={props.catalogApiPath}
        />
      </div>
    </header>
  );
}

// =============================================================================
// 3. SplitHeader — two-row utility-then-main (BTK style)
// =============================================================================

function SplitHeader(props: HeaderProps) {
  const { config, tenantHref, tenantSlug, currentUser, locale, searchOverride } = props;
  const header = config.header ?? {};
  const fallback = (config.i18n ?? DEFAULT_I18N).default;

  const showOverride = !!searchOverride;
  const showSearchHere =
    !showOverride &&
    !!(config.search?.showSearch ?? header.showSearch) &&
    header.searchPlacement !== ('hero-only' as HeaderConfig['searchPlacement']);
  const useIconVariant =
    (header.searchPlacement as 'header' | 'topbar' | 'icon' | undefined) === 'icon';

  return (
    <header style={{ background: 'var(--color-surface-card, #fff)' }}>
      <div
        style={{
          background: 'var(--color-surface-page, #f7f8fa)',
          borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)',
        }}
      >
        <div
          style={{
            ...containerRowStyle(header.maxWidth),
            height: 64,
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          <Brand
            brand={config.brand}
            tenantHref={tenantHref}
            className="lms-header-brand"
            big
            locale={locale}
            fallbackLocale={fallback}
            logoHeight={config.brand?.logoHeight}
          />
          <div
            style={{
              flex: 1,
              marginLeft: 'var(--space-8, 32px)',
              display: 'flex',
              justifyContent: useIconVariant ? 'flex-end' : 'stretch',
            }}
          >
            {showOverride && (
              <div className="lms-header-desktop-only" style={{ width: '100%', maxWidth: 720 }}>
                {searchOverride}
              </div>
            )}
            {showSearchHere && (
              <div
                className="lms-header-desktop-only"
                style={{
                  width: useIconVariant ? 'auto' : '100%',
                  maxWidth: useIconVariant ? 'none' : 720,
                }}
              >
                {useIconVariant ? (
                  <SearchInput
                    tenantHref={tenantHref}
                    tenantSlug={tenantSlug}
                    variant="icon"
                    config={config}
                    locale={locale}
                    tenantId={props.tenantId}
                    catalogApiPath={props.catalogApiPath}
                  />
                ) : (
                  <ResponsiveSearchInput
                    tenantHref={tenantHref}
                    tenantSlug={tenantSlug}
                    fillWidth
                    config={config}
                    locale={locale}
                    tenantId={props.tenantId}
                    catalogApiPath={props.catalogApiPath}
                  />
                )}
              </div>
            )}
          </div>
          <HeaderControls {...props} omitSearch />
          <MobileMenu
            config={config}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            setLocaleAction={props.setLocaleAction}
            tenantSlug={props.tenantSlug}
          />
        </div>
      </div>
      {/* Menu strip — desktop only. */}
      <div
        className="lms-header-nav-desktop"
        style={{ borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)' }}
      >
        <div
          style={{
            maxWidth: header.maxWidth === 'full' ? 'none' : 'var(--app-max-width, 1200px)',
            margin: '0 auto',
            padding: '0 24px',
            display: 'flex',
            gap: 'var(--space-6, 24px)',
            height: 44,
            alignItems: 'center',
            overflowX: 'auto',
          }}
        >
          <MenuLinks
            header={header}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            tenantSlug={props.tenantSlug}
            categoriesMenuOverride={props.categoriesMenuOverride}
          />
        </div>
      </div>
    </header>
  );
}

// =============================================================================
// 4. MinimalHeader
// =============================================================================

function MinimalHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <header
      style={{
        background: 'var(--color-surface-card, #fff)',
        borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)',
      }}
    >
      <div style={{ ...containerRowStyle(header.maxWidth), height: 56 }}>
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          className="lms-header-brand"
          locale={locale}
          fallbackLocale={fallback}
          logoHeight={config.brand?.logoHeight}
        />
        <div style={{ flex: 1 }} />
        <nav
          aria-label={chromeText(messages, 'primaryNav', locale)}
          className="lms-header-nav-desktop lms-header-menu-scroll"
          style={
            {
              ...navScrollStyle,
              display: 'flex',
              gap: 'var(--lms-menu-gap)',
              '--lms-menu-gap': header.menuGap
                ? menuGapMap[header.menuGap]
                : 'var(--space-4, 16px)',
              marginRight: 'var(--space-3, 12px)',
            } as CSSVarStyle
          }
        >
          <MenuLinks
            header={header}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            dim
            take={3}
            tenantSlug={props.tenantSlug}
            categoriesMenuOverride={props.categoriesMenuOverride}
          />
        </nav>
        <HeaderControls {...props} searchVariant="icon" />
        <MobileMenu
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          fallbackLocale={fallback}
          setLocaleAction={props.setLocaleAction}
          tenantSlug={props.tenantSlug}
          tenantId={props.tenantId}
          catalogApiPath={props.catalogApiPath}
        />
      </div>
    </header>
  );
}

// =============================================================================
// 5. CinematicHeader
// =============================================================================

const CINEMATIC_BG = 'var(--color-neutral-950, #0a0a0a)';
const CINEMATIC_BORDER = 'rgba(255,255,255,0.08)';

function CinematicHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <header
      style={{
        background: CINEMATIC_BG,
        borderBottom: `1px solid ${CINEMATIC_BORDER}`,
        // colorScheme: dark drives form-control / scrollbar UA defaults
        // toward dark. It does NOT set a text colour — that's what the
        // explicit `color` below is for: children using `color: inherit`
        // (LanguageSwitcher globe, ThemeToggle) need a real value to
        // inherit, otherwise they fall back to <body>'s text colour
        // which on a light-mode tenant is dark.
        colorScheme: 'dark',
        color: 'rgba(255,255,255,0.95)',
      }}
    >
      <div style={{ ...containerRowStyle(header.maxWidth), height: 64 }}>
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          className="lms-header-brand"
          big
          locale={locale}
          fallbackLocale={fallback}
          inverse
          logoHeight={config.brand?.logoHeight}
        />
        <nav
          aria-label={chromeText(messages, 'primaryNav', locale)}
          className="lms-header-nav-desktop lms-header-menu-scroll"
          style={
            {
              ...navScrollStyle,
              display: 'flex',
              gap: 'var(--lms-menu-gap)',
              '--lms-menu-gap': header.menuGap
                ? menuGapMap[header.menuGap]
                : 'var(--space-6, 24px)',
              marginLeft: 'var(--space-8, 32px)',
            } as CSSVarStyle
          }
        >
          <MenuLinks
            header={header}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            fallbackLocale={fallback}
            dim
            inverse
            tenantSlug={props.tenantSlug}
            categoriesMenuOverride={props.categoriesMenuOverride}
          />
        </nav>
        <div style={{ flex: 1 }} />
        <HeaderControls {...props} searchVariant="icon" inverseChrome />
        <MobileMenu
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          fallbackLocale={fallback}
          setLocaleAction={props.setLocaleAction}
          tenantSlug={props.tenantSlug}
          inverse
        />
      </div>
    </header>
  );
}

// =============================================================================
// 6. CinematicImmersiveHeader — overlays content
// =============================================================================

function CinematicImmersiveHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const overlay = header.overlay === true;

  // Server-resolve pill menu items + URLs so the client component
  // gets a serializable payload (no functions, no React nodes).
  const pillMenuItems = filterPillItems(header.menu as unknown[] | undefined, currentUser);
  const pillResolvedHrefs = pillMenuItems.map((m) => tenantHref(m.href));

  return (
    <header
      style={{
        width: '100%',
        background: overlay
          ? 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 100%)'
          : CINEMATIC_BG,
        backdropFilter: 'saturate(180%) blur(10px)',
        WebkitBackdropFilter: 'saturate(180%) blur(10px)',
        colorScheme: 'dark',
        color: 'rgba(255,255,255,0.95)',
        borderBottom: overlay ? 'none' : `1px solid ${CINEMATIC_BORDER}`,
      }}
    >
      <div style={{ ...containerRowStyle(header.maxWidth), height: 72 }}>
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          className="lms-header-brand"
          big
          locale={locale}
          fallbackLocale={fallback}
          inverse
          logoHeight={config.brand?.logoHeight}
        />
        <nav
          aria-label={chromeText(messages, 'primaryNav', locale)}
          className="lms-header-nav-desktop lms-header-menu-scroll"
          style={{
            ...navScrollStyle,
            display: 'flex',
            gap: 4,
            marginLeft: 'var(--space-8, 32px)',
            background: 'rgba(0,0,0,0.30)',
            borderRadius: 999,
            padding: 'var(--space-1, 4px) var(--space-1-5, 6px)',
          }}
        >
          <PillActiveMenuLinks
            items={pillMenuItems}
            tenantSlug={props.tenantSlug}
            resolvedHrefs={pillResolvedHrefs}
            locale={locale}
            fallbackLocale={fallback}
          />
        </nav>
        <div style={{ flex: 1 }} />
        <HeaderControls {...props} searchVariant="icon" inverseChrome />
        <MobileMenu
          config={config}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          fallbackLocale={fallback}
          setLocaleAction={props.setLocaleAction}
          tenantSlug={props.tenantSlug}
          inverse
        />
      </div>
    </header>
  );
}

// =============================================================================
// 7. SidebarHeader — vertical left rail
// =============================================================================

// Sidebar header-row height. Kept in sync with HEADER_H in
// SidebarCollapsibleHeader.tsx so the rail brand row (logo) and this
// right-column top bar (search + auth) share the same bottom-border y.
const SIDEBAR_HEADER_HEIGHT = 80;
const SIDEBAR_HEADER_BORDER = '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))';

// Shared style for the two rows of the unified top-header strip
// (sidebar brand row + right-column control bar). Keeps height /
// box-sizing / flex behaviour pixel-identical so their bottom borders
// land at the exact same y-coordinate.
const SIDEBAR_HEADER_ROW_STYLE: CSSProperties = {
  height: SIDEBAR_HEADER_HEIGHT,
  minHeight: SIDEBAR_HEADER_HEIGHT,
  maxHeight: SIDEBAR_HEADER_HEIGHT,
  boxSizing: 'border-box',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  borderBottom: SIDEBAR_HEADER_BORDER,
};

function SidebarHeader(props: HeaderProps) {
  const { config, tenantHref, currentUser, locale, tenantSlug } = props;
  const header = config.header ?? {};
  const messages = useMessages();
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const menu = (header.menu ?? []) as Array<{
    kind?: string;
    label?: LocalizedString;
    href?: string;
    badge?: LocalizedString;
    icon?: string;
    componentId?: string;
    props?: Record<string, unknown>;
    links?: Array<{
      label: LocalizedString;
      href: string;
      badge?: LocalizedString;
      icon?: string;
      requireAuth?: AuthRequirement;
      requirePerms?: string[];
      requireDynamicGroupIds?: number[];
      requireSecurityGroupIds?: number[];
    }>;
    requireAuth?: AuthRequirement;
    requirePerms?: string[];
    requireDynamicGroupIds?: number[];
    requireSecurityGroupIds?: number[];
  }>;
  const visibleMenu = menu.filter((m) =>
    isVisible(
      {
        requireAuth: m.requireAuth,
        requirePerms: m.requirePerms,
        requireDynamicGroupIds: m.requireDynamicGroupIds,
        requireSecurityGroupIds: m.requireSecurityGroupIds,
      },
      currentUser,
    ),
  );

  return (
    <aside
      className="lms-sidebar"
      style={{
        width: 220,
        flexShrink: 0,
        background: 'var(--color-surface-card, #0b0e14)',
        // No `borderRight` — the vertical separator between sidebar and
        // main is drawn by a `::after` pseudo-element starting at
        // y=SIDEBAR_HEADER_HEIGHT so the top 64px reads as one
        // continuous header band crossing the full viewport, with the
        // vertical line only appearing below where it actually divides
        // nav from content.
        position: 'sticky',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none' as CSSProperties['msOverflowStyle'],
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .lms-sidebar::after {
          content: '';
          position: absolute;
          top: ${SIDEBAR_HEADER_HEIGHT}px;
          right: 0;
          bottom: 0;
          width: 1px;
          background: var(--color-border-subtle, rgba(255,255,255,0.06));
          pointer-events: none;
        }
        .lms-sidebar::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .lms-sidebar-link:hover {
          background: var(--color-neutral-100, rgba(255,255,255,0.04)) !important;
        }
      `,
        }}
      />

      <div
        style={{
          ...SIDEBAR_HEADER_ROW_STYLE,
          padding: '0 20px',
          overflow: 'hidden',
        }}
      >
        <Brand
          brand={config.brand}
          tenantHref={tenantHref}
          locale={locale}
          fallbackLocale={fallback}
          logoHeight={config.brand?.logoHeight}
        />
      </div>

      <nav
        aria-label={chromeText(messages, 'primaryNav', locale)}
        className="lms-header-nav-desktop"
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 'var(--space-4, 16px) var(--space-2, 8px)',
          gap: 2,
          flex: 1,
        }}
      >
        {visibleMenu.map((item, i) => {
          if (item.kind === 'component') {
            if (!item.componentId) return null;
            const Comp = getMenuComponent(item.componentId);
            if (!Comp) return null;
            const mergedProps = mergeMenuComponentProps(item as unknown as MenuComponentEntry);
            // Sidebar rows always show icons unconditionally (same as
            // plain `kind: 'link'` sidebar entries) — `showMenuIcons` only
            // gates top-bar styles, so force it on here regardless of the
            // header's own setting.
            mergedProps['showMenuIcons'] = true;
            if (item.componentId === 'categoriesMenu' && props.categoriesMenuOverride) {
              mergedProps['feedSource'] = props.categoriesMenuOverride.feedSource;
            }
            return (
              <Comp
                key={i}
                placement="sidebar"
                collapsed={false}
                tenantSlug={tenantSlug}
                locale={locale}
                fallbackLocale={fallback}
                currentUser={currentUser}
                props={mergedProps}
              />
            );
          }

          if (item.kind === 'group') {
            const groupLinks = (item.links ?? []).filter((l) =>
              isVisible(
                {
                  requireAuth: l.requireAuth,
                  requirePerms: l.requirePerms,
                  requireDynamicGroupIds: l.requireDynamicGroupIds,
                  requireSecurityGroupIds: l.requireSecurityGroupIds,
                },
                currentUser,
              ),
            );
            return (
              <div key={i}>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 700,
                    letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                    textTransform: 'uppercase',
                    color: 'var(--color-text-secondary, #6b7280)',
                    padding: 'var(--space-3-5, 14px) var(--space-3, 12px) var(--space-1-5, 6px)',
                  }}
                >
                  {t(item.label, locale, fallback)}
                </div>
                {groupLinks.map((link, j) => {
                  const linkLabel = t(link.label, locale, fallback);
                  const linkBadge = t(link.badge, locale, fallback);
                  const linkIconName = link.icon || guessSidebarIcon(linkLabel);
                  return (
                    <PortalLink
                      key={j}
                      href={tenantHref(link.href)}
                      className="lms-sidebar-link"
                      style={sidebarLinkStyle}
                    >
                      <span style={sidebarLinkIconStyle}>
                        <BlockIcon value={linkIconName} size={18} />
                      </span>
                      <span>{linkLabel}</span>
                      {linkBadge && <span style={sidebarBadgeStyle}>{linkBadge}</span>}
                    </PortalLink>
                  );
                })}
              </div>
            );
          }

          if (!item.href) return null;
          const label = t(item.label, locale, fallback);
          const badge = t(item.badge, locale, fallback);
          const iconName = item.icon || guessSidebarIcon(label);
          return (
            <PortalLink
              key={i}
              href={tenantHref(item.href)}
              className="lms-sidebar-link"
              style={sidebarLinkStyle}
            >
              <span style={sidebarLinkIconStyle}>
                <BlockIcon value={iconName} size={18} />
              </span>
              <span>{label}</span>
              {badge && <span style={sidebarBadgeStyle}>{badge}</span>}
            </PortalLink>
          );
        })}
      </nav>

      {/* Compact mode: sign-in / theme / language switcher live inside
          the sidebar near the bottom. Otherwise these live in
          SidebarTopBar above main content. */}
      {header.compact && (
        <div
          style={{
            padding: 'var(--space-3, 12px) var(--space-4, 16px)',
            borderTop: '1px solid var(--color-border-subtle, rgba(255,255,255,0.06))',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <HeaderControls {...props} omitSearch />
        </div>
      )}

      <div style={{ padding: '0 20px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <HeaderActions
          header={header}
          locale={locale}
          i18nDefault={fallback}
          currentUser={currentUser}
          tenantHref={tenantHref}
        />
      </div>
    </aside>
  );
}

const sidebarLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: 'var(--space-2-5, 10px) var(--space-3, 12px)',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 500,
  color: 'var(--color-text-primary, #e5e7eb)',
  textDecoration: 'none',
  transition: 'background 120ms ease, color 120ms ease',
};

const sidebarLinkIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flexShrink: 0,
  color: 'var(--color-text-secondary, #94a3b8)',
};

const sidebarBadgeStyle: CSSProperties = {
  marginLeft: 'auto',
  background: 'var(--color-brand-500, #3b5bdb)',
  color: 'var(--color-text-on-brand, #fff)',
  fontSize: 'var(--font-size-xs, 12px)',
  fontWeight: 700,
  padding: 'var(--space-0-5, 2px) var(--space-1-5, 6px)',
  borderRadius: 'var(--radius-sm, 4px)',
};

// =============================================================================
// SidebarTopBar — horizontal control strip above main content
//                 (rendered when header.style === 'sidebar')
// =============================================================================

/**
 * Mobile-only sticky bar shown above main on sidebar layouts. The
 * desktop sidebar is hidden under 768px; this provides brand + a
 * hamburger that opens a drawer carrying the same items.
 */
export function SidebarMobileBar({
  config,
  tenantHref,
  currentUser,
  locale,
  setLocaleAction,
  tenantSlug,
  tenantId,
  catalogApiPath,
}: {
  config: ShellConfig;
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  locale: string;
  setLocaleAction?: HeaderProps['setLocaleAction'];
  tenantSlug: string;
  tenantId?: string | number;
  catalogApiPath?: string;
}) {
  // Sidebar-layout companion of the rail — gated by the same
  // header-level "Visible to" rule so a hidden header doesn't leave
  // its mobile bar stranded on small screens.
  if (!isHeaderVisible(config, currentUser)) return null;
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <div
      className="lms-sidebar-mobile-bar"
      style={{
        // Hidden by default; CSS media query flips display:flex
        // under 768px. Inline default `none` keeps the desktop
        // render unaffected even if globals.css doesn't load.
        display: 'none',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        // Match the sidebar brand row: fixed 64px height, horizontal padding only.
        height: 64,
        boxSizing: 'border-box' as const,
        padding: '0 15px',
        background: 'var(--color-surface-card, #18181b)',
        borderBottom: '1px solid var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <Brand
        brand={config.brand}
        tenantHref={tenantHref}
        className="lms-header-brand"
        locale={locale}
        fallbackLocale={fallback}
        logoHeight={config.brand?.logoHeight ?? 36}
      />
      <MobileMenu
        config={config}
        tenantHref={tenantHref}
        currentUser={currentUser}
        locale={locale}
        fallbackLocale={fallback}
        tenantSlug={tenantSlug}
        setLocaleAction={setLocaleAction}
        tenantId={tenantId}
        catalogApiPath={catalogApiPath}
      />
    </div>
  );
}

export function SidebarTopBar(props: HeaderProps) {
  const { config, tenantHref, tenantSlug, locale, searchOverride } = props;
  // Same header-level "Visible to" gate as the rail itself.
  if (!isHeaderVisible(config, props.currentUser)) return null;
  const header = config.header ?? {};
  const i18n = config.i18n ?? DEFAULT_I18N;

  const showSwitcher = (i18n.showSwitcher ?? false) && (i18n.available?.length ?? 1) > 1;
  const anyControl =
    ((config.search?.showSearch ?? header.showSearch) &&
      header.searchPlacement !== ('hero-only' as HeaderConfig['searchPlacement'])) ||
    showSwitcher ||
    header.showThemeToggle ||
    (header.showSignIn ?? true) ||
    (header.showProfile ?? true);

  const isCollapsible = header.style === 'sidebar-collapsible';

  if (!anyControl && !isCollapsible) return null;

  // Centre-column search. Mirrors HeaderControls' own gating, but the
  // search is lifted out and rendered in a centred middle column so it
  // sits in the true centre of the bar with the auth/controls cluster
  // pinned right (equal-flex spacers on each side). Icon-variant search
  // stays inside HeaderControls — a centred magnifier reads oddly.
  const useIconVariant =
    (header.searchPlacement as 'header' | 'topbar' | 'icon' | undefined) === 'icon';
  const showOverride = !!searchOverride;
  const showDefaultSearch =
    !showOverride &&
    !!(config.search?.showSearch ?? header.showSearch) &&
    header.searchPlacement !== ('hero-only' as HeaderConfig['searchPlacement']);
  const centeredSearch = useIconVariant ? null : showOverride ? (
    searchOverride
  ) : showDefaultSearch ? (
    <SearchInput
      tenantHref={tenantHref}
      tenantSlug={tenantSlug}
      variant="pill"
      fillWidth
      config={config}
      locale={locale}
      tenantId={props.tenantId}
      catalogApiPath={props.catalogApiPath}
    />
  ) : null;

  return (
    <div
      style={{
        ...SIDEBAR_HEADER_ROW_STYLE,
        gap: 'var(--space-3, 12px)',
        padding: '0 16px 0 8px',
        background: 'var(--color-surface-card, #0b0e14)',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      {/* Left spacer — equal flex to the right cluster keeps the search
          in the bar's true centre regardless of the auth cluster width. */}
      <div style={{ flex: 1 }} />
      {centeredSearch && (
        <div
          style={{
            flex: '0 1 560px',
            width: '100%',
            maxWidth: 560,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {centeredSearch}
        </div>
      )}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {/* omitSearch when we've already centred it; icon-variant search
            still renders inside HeaderControls here. */}
        <HeaderControls {...props} omitSearch={!!centeredSearch} />
      </div>
    </div>
  );
}

// =============================================================================
// TopBar — optional announcement strip above any header style
// =============================================================================

export interface TopBarProps {
  config: ShellConfig;
  tenantHref: HeaderProps['tenantHref'];
  currentUser: CurrentUser | null;
  locale: string;
}

export function TopBar({ config, tenantHref, currentUser, locale }: TopBarProps) {
  // Accept either canonical-cased `topbar` or legacy `topBar` — operators
  // whose configs predate the rename keep working.
  const tb = config.topbar ?? config.topBar;
  if (!tb) return null;
  if (tb.visible === false) return null;
  if (
    !isVisible(
      {
        requireAuth: (tb as { requireAuth?: AuthRequirement }).requireAuth,
        requirePerms: (tb as { requirePerms?: string[] }).requirePerms,
        requireDynamicGroupIds: (tb as { requireDynamicGroupIds?: number[] })
          .requireDynamicGroupIds,
        requireSecurityGroupIds: (tb as { requireSecurityGroupIds?: number[] })
          .requireSecurityGroupIds,
      },
      currentUser,
    )
  ) {
    return null;
  }
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const message = t(tb.message, locale, fallback);
  if (!message) return null;
  const label = t(tb.label, locale, fallback);
  const ctaLabel = t(tb.ctaLabel, locale, fallback);

  const bg =
    tb.tone === 'info'
      ? 'var(--color-brand-500, #3b5bdb)'
      : tb.tone === 'warning'
        ? 'var(--color-warning, #f59e0b)'
        : tb.tone === 'danger'
          ? 'var(--color-danger-fg, #ef4444)'
          : tb.tone === 'success'
            ? 'var(--color-success, #10b981)'
            : tb.tone === 'neutral'
              ? 'var(--color-surface-page, #f7f8fa)'
              : 'var(--color-neutral-900, #18181b)';
  const fg =
    tb.tone === 'neutral'
      ? 'var(--color-text-primary, #0f1218)'
      : 'var(--color-text-on-brand, #fff)';

  return (
    <div
      style={{
        background: bg,
        color: fg,
        fontSize: 'var(--font-size-xs, 12px)',
        padding: 'var(--space-2, 8px) 0',
        borderBottom:
          tb.tone === 'neutral' ? '1px solid var(--color-neutral-200, #e5e7eb)' : 'none',
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3, 12px)',
        }}
      >
        {label && (
          <span
            style={{
              fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-widest, 0.12em)',
              fontSize: 'var(--font-size-xs, 12px)',
              opacity: 0.65,
            }}
          >
            {label}
          </span>
        )}
        <span style={{ flex: 1 }}>{message}</span>
        {ctaLabel && (
          <PortalLink
            href={tenantHref(tb.ctaHref)}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            {ctaLabel}
          </PortalLink>
        )}
      </div>
    </div>
  );
}
